import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  FileText, Users, Receipt, Package, Mail, CheckCircle, XCircle,
  Clock, List, Palette, CreditCard, Shield,
  ArrowRight, Smartphone, Zap, Send, Loader2, Sparkles, Truck, Play, Calculator, Ticket, FolderOpen, Mic, MapPin, Phone, PhoneCall,
  Target, Banknote, ShieldCheck, Globe, FolderKanban, FileSignature, Flame
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import axios from "axios";
import { useToast } from "../hooks/use-toast";
import SEOHead from "../components/SEOHead";
import WalkthroughGuide from "../components/WalkthroughGuide";
import { useAuth } from "../contexts/AuthContext";
import { PublicNav } from "../components/PublicNav";
import { TrustCount } from "../components/TrustCount";
import { useDiscountFromLink } from "../hooks/useDiscountFromLink";
import PaidCardCapture from "../components/PaidCardCapture";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { startDemo } = useAuth();
  const discountLink = useDiscountFromLink();
  const [loadingTier, setLoadingTier] = useState(null);
  const [loadingDemo, setLoadingDemo] = useState(false);
  const [earlyBird, setEarlyBird] = useState({ is_active: false, remaining_spots: 0, discount_percentage: 50 });
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  
  // Coupon state
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponValidating, setCouponValidating] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  
  // Demo tabs state
  const [demoTab, setDemoTab] = useState('customers');
  const [supplierCompanyName, setSupplierCompanyName] = useState('');
  const [supplierEmail, setSupplierEmail] = useState('');
  const [supplierDemoLoading, setSupplierDemoLoading] = useState(false);

  // Paid card capture (in-app SDK modal for logged-in upgrades from landing)
  const [showPaidCardModal, setShowPaidCardModal] = useState(false);
  
  // Trade notify me state
  const [showTradeNotifyModal, setShowTradeNotifyModal] = useState(false);
  const [selectedTradeForNotify, setSelectedTradeForNotify] = useState(null);
  const [tradeNotifyEmail, setTradeNotifyEmail] = useState('');
  const [tradeNotifyName, setTradeNotifyName] = useState('');
  const [tradeNotifySubmitting, setTradeNotifySubmitting] = useState(false);
  const [tradeNotifySuccess, setTradeNotifySuccess] = useState(false);
  const [patLaunched, setPatLaunched] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/feature-toggles`)
      .then((r) => setPatLaunched(r.data?.pat_module_launched !== false))
      .catch(() => {});
  }, []);

  // Fetch early bird status
  useEffect(() => {
    const fetchEarlyBirdStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/early-bird-status`);
        setEarlyBird(response.data);
      } catch (error) {
        console.error("Error fetching early bird status:", error);
      }
    };
    fetchEarlyBirdStatus();
  }, []);

  /**
   * Resolves the active site-wide promo for pricing displays.
   *
   * Precedence (most-specific wins):
   *   1. Discount-link visitor (came via /go/<slug>) — already in localStorage
   *   2. Admin-toggled early-bird promo (site-wide for all visitors)
   *
   * NOTE: This is for DISPLAY only. Coupon codes entered at checkout are
   * separate and stack on top (see appliedCoupon flow). Promo codes never
   * affect what visitors see on the homepage — those only resolve at checkout.
   *
   * Returns null if no promo active, else { pct, label, source }.
   */
  const activePromo = (() => {
    if (discountLink && discountLink.pct > 0) {
      return { pct: discountLink.pct, label: discountLink.name || `${discountLink.pct}% off`, source: "link" };
    }
    if (earlyBird?.is_active && earlyBird.discount_percentage > 0) {
      return {
        pct: earlyBird.discount_percentage,
        label: earlyBird.banner_title || `${earlyBird.discount_percentage}% off`,
        source: "early_bird",
      };
    }
    return null;
  })();

  /**
   * Apply the active promo to a base price. Matches the backend's
   * int(base_amount * (1 - pct/100)) floor-on-pence rounding so the
   * homepage display matches what gets charged at checkout.
   */
  const applyPromo = (basePrice) => {
    if (!activePromo || !basePrice || basePrice <= 0) return null;
    const pence = Math.round(basePrice * 100);
    const discountedPence = Math.floor(pence * (1 - activePromo.pct / 100));
    return discountedPence / 100;
  };

  /** Format a price as £x.xx (or £x if whole). */
  const fmtPrice = (p) => {
    if (p === 0) return "£0";
    return `£${p.toFixed(2)}`;
  };
  
  // Supplier demo handler
  const handleSupplierDemo = async (e) => {
    e.preventDefault();
    setSupplierDemoLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/demo-preview/supplier-request`, {
        company_name: supplierCompanyName,
        email: supplierEmail,
      });
      
      toast({
        title: "Check your inbox!",
        description: "We've sent you a sample materials request email.",
      });
      
      // Clear form
      setSupplierCompanyName('');
      setSupplierEmail('');
      
    } catch (error) {
      console.error("Supplier demo error:", error);
      toast({
        title: "Error",
        description: "Failed to send demo email. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSupplierDemoLoading(false);
    }
  };

  const handleTryDemo = async () => {
    setLoadingDemo(true);
    try {
      await startDemo();
      toast({
        title: "Welcome to the Demo!",
        description: "Explore the app freely. Your session lasts 30 minutes."
      });
      // Small delay to ensure auth state is updated, then reload to trigger protected routes
      setTimeout(() => {
        window.location.href = "/";
      }, 100);
    } catch (error) {
      console.error("Failed to start demo:", error);
      toast({
        title: "Demo Error",
        description: "Failed to start demo session. Please try again.",
        variant: "destructive"
      });
      setLoadingDemo(false);
    }
  };

  const handleSubscribe = async (tier) => {
    const token = localStorage.getItem("token");
    
    if (!token) {
      // User needs to register/login first - pass the tier so we can redirect after
      toast({
        title: "Account required",
        description: "Please create an account to subscribe.",
        variant: "default"
      });
      navigate(`/register?plan=${tier}`);
      return;
    }

    // Show coupon dialog before checkout
    setSelectedTier(tier);
    setCouponCode("");
    setAppliedCoupon(null);
    setShowCouponDialog(true);
  };

  const handleStartTrial = (tier) => {
    // Card-on-signup is required for trials per product spec.
    // Always route through the Register page which then opens the
    // TrialCardCapture modal (Square Web Payments SDK → /start-trial-with-card).
    // The legacy no-card endpoint has been retired.
    navigate(`/register?plan=${tier}&trial=true`);
  };

  // Handle trade notify me submission
  const handleTradeNotifySubmit = async () => {
    if (!tradeNotifyEmail || !tradeNotifyEmail.includes('@')) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return;
    }
    
    setTradeNotifySubmitting(true);
    try {
      await axios.post(`${API_URL}/api/certificates/notify-me/public`, {
        trade_module: selectedTradeForNotify?.id,
        email: tradeNotifyEmail,
        name: tradeNotifyName || null,
        notify_type: selectedTradeForNotify?.notifyType
      });
      
      setTradeNotifySuccess(true);
      toast({
        title: "You're on the list! 🎉",
        description: `We'll notify you when ${selectedTradeForNotify?.name} is available!`
      });
      
      // Reset after success
      setTimeout(() => {
        setShowTradeNotifyModal(false);
        setTradeNotifySuccess(false);
        setTradeNotifyEmail('');
        setTradeNotifyName('');
      }, 2000);
    } catch (error) {
      console.error("Notify error:", error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to register. Please try again.",
        variant: "destructive"
      });
    } finally {
      setTradeNotifySubmitting(false);
    }
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponValidating(true);
    try {
      const response = await axios.post(`${API_URL}/api/validate-coupon`, {
        code: couponCode.trim(),
        tier: selectedTier
      });
      
      if (response.data.valid) {
        setAppliedCoupon({
          code: couponCode.trim().toUpperCase(),
          discount_percentage: response.data.discount_percentage,
          description: response.data.description
        });
        toast({
          title: "Coupon Applied!",
          description: `${response.data.discount_percentage}% discount will be applied`
        });
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid coupon code";
      toast({
        title: "Invalid Coupon",
        description: message,
        variant: "destructive"
      });
      setAppliedCoupon(null);
    } finally {
      setCouponValidating(false);
    }
  };

  const proceedToCheckout = async () => {
    const token = localStorage.getItem("token");
    setShowCouponDialog(false);

    if (!token) {
      // Not logged in — send them through the register flow with the
      // selected plan; Register.jsx now opens the SDK modal for paid plans.
      navigate(`/register?plan=${selectedTier}${appliedCoupon ? `&coupon=${appliedCoupon.code}` : ''}`);
      return;
    }

    // Logged-in upgrade: open in-app SDK modal which charges immediately
    // AND creates the recurring Square Subscription in one shot.
    setShowPaidCardModal(true);
  };

  const handlePaidSubscribed = () => {
    setShowPaidCardModal(false);
    toast({
      title: "Subscription active!",
      description: "Welcome aboard.",
    });
    navigate("/?subscribed=1");
  };

  const features = [
    // Top 3 hero features
    {
      icon: <List className="w-6 h-6" />,
      title: "Bulk Add Items",
      description: "Paste a simple list of items and we'll parse them automatically. Add 20 line items in seconds, not minutes.",
      highlight: true
    },
    {
      icon: <Truck className="w-6 h-6" />,
      title: "Supplier Quote Requests",
      description: "Email suppliers for materials quotes. They submit prices, you apply with your markup automatically.",
      highlight: true
    },
    {
      icon: <Mic className="w-6 h-6" />,
      title: "Voice Quotes",
      description: "Create quotes in under 15 seconds. Just say the customer, job, and price - done. Perfect for on-site quoting.",
      highlight: true,
      badge: "BETA"
    },
    // Remaining features
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Professional Quotes",
      description: "Create beautiful quotes/estimates with your branding. Customers can accept with one click."
    },
    {
      icon: <Receipt className="w-6 h-6" />,
      title: "Smart Invoicing",
      description: "Convert accepted quotes to invoices instantly. Track payments, deposits, and balances."
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: "One-Click Card Payments",
      description: "Get paid faster. Customers can pay invoices instantly via card directly from their email.",
      highlight: true
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: "Materials Tracking",
      description: "Add materials costs separately from labour. Keep your pricing transparent and professional."
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Mileage Calculator",
      description: "Calculate travel costs with real UK mileage rates. Add journey costs directly to quotes and invoices.",
      highlight: true,
      badge: "NEW"
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email Delivery",
      description: "Send quotes and invoices directly to customers. Track when they're opened and viewed."
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Custom Branding",
      description: "Your logo, your colors, your message. Four PDF templates to choose from."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Recurring Invoices",
      description: "Set up recurring billing for regular customers. Generate invoices automatically."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Customer Directory",
      description: "Keep all your customer details in one place. Quick access to their quote and invoice history."
    },
    {
      icon: <Calculator className="w-6 h-6" />,
      title: "Accountant Access",
      description: "Invite your accountant to securely view and download your quotes, invoices, and receipts. You control exactly what they can see.",
      highlight: true
    }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Add Your Customer",
      description: "Enter customer details once. They're saved for all future quotes and invoices."
    },
    {
      step: "2",
      title: "Create a Quote",
      description: "Use bulk add to quickly list your items, or select from your saved item catalog."
    },
    {
      step: "3",
      title: "Send & Track",
      description: "Email directly to your customer. See when they open and view your quote."
    },
    {
      step: "4",
      title: "Get Paid",
      description: "Customer accepts? Convert to invoice with one click. Track payments until fully paid."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="QuoteSorted — Quoting, Invoicing & Certificates for UK Tradespeople"
        description="The all-in-one app for electricians and plumbers. Professional quotes in 15 seconds, smart invoicing, EIC/EICR certificates, floor plans and more. Free to start."
        path="/landing"
      />
      {/* Navigation */}
      <PublicNav
        onHowItWorks={() => setShowWalkthrough(true)}
        onComingSoonNotify={() => {
          setSelectedTradeForNotify({ id: 'coming_soon', name: 'PAT, HVAC & Fire Safety', notifyType: 'coming_soon' });
          setShowTradeNotifyModal(true);
        }}
      />

      {/* Hero Section — outcome-led, no big dashboard screenshot */}
      <main>
      <section className="pt-28 pb-12 sm:pt-36 sm:pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 via-white to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            {/* AI Receptionist announcement pill */}
            <Link
              to="/features/ai-receptionist"
              className="inline-flex items-center gap-2 mb-7 rounded-full border border-slate-200 bg-white pl-1 pr-4 py-1 text-sm font-medium text-slate-700 hover:border-cyan-300 hover:shadow-md transition-all group"
              data-testid="landing-receptionist-pill"
            >
              <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-cyan-500 to-fuchsia-500 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-white">
                <Sparkles className="w-3 h-3" /> New
              </span>
              <span>Meet Victoria — your AI Phone Receptionist</span>
              <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-cyan-600 group-hover:translate-x-0.5 transition-all" />
            </Link>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05] tracking-tight" data-testid="hero-headline">
              Less time on paperwork.
              <br />
              <span className="bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 bg-clip-text text-transparent">More time on the tools.</span>
            </h1>

            <p className="mt-7 text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              QuoteSorted is a kit of tools that does your evening admin <em>for</em> you.
              Quotes, invoices, certificates, AI phone answering, your own website, card payments —
              one clean app, so you can knock off at five.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Link to="/register">
                <Button size="lg" className="w-full sm:w-auto gap-2 text-base px-8 bg-orange-600 hover:bg-orange-700 text-white shadow-lg shadow-orange-500/20" data-testid="hero-get-started-btn">
                  Get Started Free
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              {/* "Try Demo" sandbox CTA hidden during early access — re-enable later
                  when the demo flow is polished. Logic + handler kept intact below. */}
              {false && (
              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto text-base px-8 gap-2 border-slate-300 text-slate-700 hover:bg-slate-50"
                onClick={handleTryDemo}
                disabled={loadingDemo}
                data-testid="hero-try-demo-btn"
              >
                {loadingDemo ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Play className="w-5 h-5" />
                )}
                Try Demo
              </Button>
              )}
            </div>
            <p className="mt-5 text-sm text-slate-500" data-testid="hero-no-cc-line">
              <span className="text-emerald-600 font-semibold">Free forever plan</span> · No card required · All core tools on every plan
            </p>
            <div className="mt-6 flex justify-center" data-testid="hero-trust-count">
              <TrustCount variant="light" scope="app" />
            </div>
          </div>
        </div>
      </section>

      {/* The Kit — 4 outcome-led groups (replaces big dashboard screenshot) */}
      <section className="pb-20 sm:pb-28 px-4 sm:px-6 lg:px-8 bg-white" data-testid="the-kit-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-3">Everything in one app</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              The kit. Built around how you actually work.
            </h2>
            <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              The core kit comes with every plan. Higher tiers unlock more volume and premium tools.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {[
              {
                icon: Target, accent: "cyan", title: "Win the work",
                tagline: "Bring new jobs in while you're on the tools.",
                bullets: [
                  { icon: Mic, label: "AI Phone Receptionist", path: "/features/ai-receptionist" },
                  { icon: Globe, label: "Your own website", path: "/features/pages" },
                  { icon: MapPin, label: "Local directory listing", path: "/find" },
                ],
              },
              {
                icon: FileSignature, accent: "violet", title: "Quote & invoice",
                tagline: "From enquiry to signed quote — fast.",
                bullets: [
                  { icon: Mic, label: "Voice admin (Owner Mode) — coming soon", path: "/features/owner-mode" },
                  { icon: FileText, label: "Quotes & invoices, one-click", path: "/features/quoting" },
                  { icon: FolderKanban, label: "Multi-page proposals", path: "/features/projects" },
                ],
              },
              {
                icon: Banknote, accent: "emerald", title: "Get paid",
                tagline: "Chase less. Collect sooner.",
                bullets: [
                  { icon: CreditCard, label: "Card payments by Square", path: "/features/additional" },
                  { icon: Clock, label: "Recurring invoices", path: "/features/additional" },
                  { icon: Receipt, label: "Expenses & accountant export", path: "/features/additional" },
                ],
              },
              {
                icon: ShieldCheck, accent: "amber", title: "Stay compliant",
                tagline: "Certs, audit trail, always inspection-ready.",
                betaNote: patLaunched
                  ? "PAT testing now live · Electrical available now · Heating & plumbing in closed beta."
                  : "Heating & plumbing in closed beta — PAT testing in development — Electrical available now.",
                bullets: [
                  { icon: Zap, label: "Electrical certificates", path: "/trade-certificates" },
                  { icon: Flame, label: "Heating & plumbing", path: "/trade-certificates/heating-plumbing", beta: true },
                  { icon: Shield, label: "PAT testing", path: "/trade-certificates/pat-testing", devBadge: !patLaunched, liveBadge: patLaunched },
                  { icon: Shield, label: "Audit trail on every action", path: "/features" },
                ],
              },
            ].map((g) => {
              const palette = {
                cyan:    { ring: "ring-cyan-200/70 hover:ring-cyan-300", icon: "text-cyan-600", chip: "bg-cyan-50 border-cyan-200/60", title: "text-cyan-700" },
                violet:  { ring: "ring-violet-200/70 hover:ring-violet-300", icon: "text-violet-600", chip: "bg-violet-50 border-violet-200/60", title: "text-violet-700" },
                emerald: { ring: "ring-emerald-200/70 hover:ring-emerald-300", icon: "text-emerald-600", chip: "bg-emerald-50 border-emerald-200/60", title: "text-emerald-700" },
                amber:   { ring: "ring-amber-200/70 hover:ring-amber-300", icon: "text-amber-600", chip: "bg-amber-50 border-amber-200/60", title: "text-amber-700" },
              }[g.accent];
              return (
                <div
                  key={g.title}
                  className={`rounded-2xl bg-white ring-1 ${palette.ring} p-5 sm:p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-slate-200/50`}
                  data-testid={`kit-group-${g.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className={`w-11 h-11 rounded-xl border ${palette.chip} flex items-center justify-center mb-4`}>
                    <g.icon className={`w-5 h-5 ${palette.icon}`} strokeWidth={2.2} />
                  </div>
                  <h3 className={`text-lg font-bold ${palette.title} mb-1.5`}>{g.title}</h3>
                  <p className="text-sm text-slate-500 mb-4 leading-snug">{g.tagline}</p>
                  <div className="space-y-2">
                    {g.bullets.map((b) => (
                      <Link
                        key={b.label}
                        to={b.path}
                        className="flex items-center gap-2.5 text-sm text-slate-700 hover:text-slate-950 group"
                      >
                        <b.icon className={`w-3.5 h-3.5 ${palette.icon} flex-shrink-0`} strokeWidth={2.2} />
                        <span className="group-hover:underline underline-offset-2 decoration-slate-300 flex items-center gap-1.5">
                          {b.label}
                          {b.beta && (
                            <span className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 leading-none">BETA</span>
                          )}
                          {b.devBadge && (
                            <span className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-300 leading-none">DEV</span>
                          )}
                          {b.liveBadge && (
                            <span className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 leading-none">LIVE</span>
                          )}
                        </span>
                      </Link>
                    ))}
                  </div>
                  {g.betaNote && (
                    <p className="text-[11px] text-amber-700 italic mt-3 pt-3 border-t border-amber-100">
                      {g.betaNote}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 bg-gray-50 border-y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-16 text-gray-400">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium">Secure & Encrypted</span>
            </div>
            <div className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              <span className="text-sm font-medium">Works on Mobile</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              <span className="text-sm font-medium">Lightning Fast</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm font-medium">UK Focused</span>
            </div>
          </div>
        </div>
      </section>


      {/* Interactive Demo Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
            <Mail className="w-4 h-4" />
            Interactive Demo
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            See How It Works
          </h2>
          
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Experience the real emails your customers and suppliers will receive. Enter your details and we'll send you sample communications.
          </p>
          
          {/* Demo Tabs */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 sm:p-8">
            {/* Tab Buttons */}
            <div className="inline-flex bg-white rounded-lg p-1 shadow-sm mb-8">
              <button
                onClick={() => setDemoTab('customers')}
                className={`px-6 py-2.5 rounded-md text-sm font-medium transition-all ${
                  demoTab === 'customers' 
                    ? 'bg-blue-600 text-white shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="demo-tab-customers"
              >
                <Users className="w-4 h-4 inline mr-2" />
                For Customers
              </button>
              <button
                onClick={() => setDemoTab('suppliers')}
                className={`px-6 py-2.5 rounded-md text-sm font-medium transition-all ${
                  demoTab === 'suppliers' 
                    ? 'bg-orange-500 text-white shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="demo-tab-suppliers"
              >
                <Truck className="w-4 h-4 inline mr-2" />
                For Suppliers
              </button>
            </div>
            
            {/* Customer Demo Tab Content */}
            {demoTab === 'customers' && (
              <>
                <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
                  <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Sample Quote</h3>
                    <p className="text-xs sm:text-sm text-gray-500">Professional layout with your branding</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <Receipt className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Sample Invoice</h3>
                    <p className="text-xs sm:text-sm text-gray-500">With payment button & bank details</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <FolderOpen className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Proposals</h3>
                    <p className="text-xs sm:text-sm text-gray-500">Multi-page with floor plans</p>
                  </div>
                </div>
                
                <Button 
                  size="lg"
                  onClick={() => navigate('/demo-preview')}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  data-testid="try-customer-demo-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Try Customer Demo
                </Button>
                
                <p className="text-sm text-gray-500 mt-4">
                  Receive 3 sample emails - quote, invoice, and proposal
                </p>
              </>
            )}
            
            {/* Supplier Demo Tab Content */}
            {demoTab === 'suppliers' && (
              <>
                <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
                  <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">1. Receive Request</h3>
                    <p className="text-xs sm:text-sm text-gray-500">Get materials list from tradesperson</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <Package className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">2. Submit Quote</h3>
                    <p className="text-xs sm:text-sm text-gray-500">Enter price or upload PDF quote</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">3. Get Accepted</h3>
                    <p className="text-xs sm:text-sm text-gray-500">Tradesperson reviews & accepts</p>
                  </div>
                </div>
                
                {/* Supplier Demo Form */}
                <div className="bg-white rounded-xl p-6 max-w-md mx-auto">
                  <form onSubmit={handleSupplierDemo} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Your Company Name
                      </label>
                      <Input
                        placeholder="e.g. ABC Electrical Supplies"
                        value={supplierCompanyName}
                        onChange={(e) => setSupplierCompanyName(e.target.value)}
                        required
                        data-testid="supplier-company-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        Email Address
                      </label>
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={supplierEmail}
                        onChange={(e) => setSupplierEmail(e.target.value)}
                        required
                        data-testid="supplier-email-input"
                      />
                    </div>
                    <Button 
                      type="submit"
                      className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                      disabled={supplierDemoLoading}
                      data-testid="try-supplier-demo-btn"
                    >
                      {supplierDemoLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Truck className="w-4 h-4 mr-2" />
                          Try Supplier Demo
                        </>
                      )}
                    </Button>
                  </form>
                </div>
                
                <p className="text-sm text-gray-500 mt-4">
                  Receive a sample materials request email
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Slim "See pricing" CTA — pricing detail lives on /pricing now */}
      <section id="pricing" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-zinc-50">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center rounded-sm border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-[11px] font-bold text-zinc-950 uppercase tracking-[0.2em]">Pricing</span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            One simple price. The whole core kit included.
          </h2>
          {activePromo ? (
            <>
              <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold uppercase tracking-wider" data-testid="landing-pricing-promo-badge">
                <Sparkles className="w-3.5 h-3.5" /> {activePromo.label} applied
              </div>
              <p className="text-lg text-gray-600 mb-10" data-testid="landing-pricing-teaser">
                Free £0 · Solo <span className="text-gray-400 line-through mr-1">£9.99</span><span className="text-emerald-700 font-semibold">{fmtPrice(applyPromo(9.99))}</span>
                {' '}·{' '}
                <strong>Pro <span className="text-gray-400 line-through mr-1">£29.99</span><span className="text-emerald-700">{fmtPrice(applyPromo(29.99))}</span></strong>
                {' '}·{' '}
                Crew <span className="text-gray-400 line-through mr-1">£74.99</span><span className="text-emerald-700 font-semibold">{fmtPrice(applyPromo(74.99))}</span> (Q4 2026). Voice baked into Pro &amp; Crew. No surprise add-ons.
              </p>
            </>
          ) : (
            <p className="text-lg text-gray-600 mb-10" data-testid="landing-pricing-teaser">
              Free £0 · Solo £9.99 · <strong>Pro £29.99</strong> · Crew £74.99 (Q4 2026). Voice baked into Pro &amp; Crew. No surprise add-ons.
            </p>
          )}
          <Link to="/pricing">
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25 px-8 gap-2"
              data-testid="view-full-pricing-btn"
            >
              See full pricing
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Simplify Your Quotes & Invoices?
          </h2>
          <p className="text-lg text-gray-300 mb-10">
            Join hundreds of tradespeople and small businesses who've streamlined their billing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="w-full sm:w-auto gap-2 text-lg px-8 bg-white text-gray-900 hover:bg-gray-100">
                Start Free - No Card Required
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <a href="#pricing">
              <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 border-gray-600 text-gray-300 hover:bg-gray-800">
                View Pricing
              </Button>
            </a>
          </div>
          <p className="mt-6 text-gray-400 text-sm">
            Free plan includes 2 customers, 2 quotes & 2 invoices per month
          </p>
        </div>
      </section>

      </main>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img 
                src={`${API_URL}/api/uploads/quotesorted_logo.png`} 
                alt="QuoteSorted" 
                className="h-12 w-auto brightness-0 invert"
                loading="lazy"
              />
            </div>
            <div className="flex items-center gap-6 text-gray-400 text-sm">
              <Link to="/find" className="hover:text-white transition-colors">Find a Tradesperson</Link>
              <button 
                onClick={() => setShowWalkthrough(true)} 
                className="hover:text-white transition-colors"
              >
                How it works
              </button>
              <Link
                to="/roadmap"
                className="hover:text-white transition-colors"
                data-testid="landing-footer-roadmap"
              >
                Roadmap
              </Link>
              <Link to="/login" className="hover:text-white transition-colors">Sign In</Link>
              <Link to="/register" className="hover:text-white transition-colors">Register</Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
            <div className="flex justify-center gap-4 mb-4">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            </div>
            © {new Date().getFullYear()} QuoteSorted. All rights reserved. Built in the UK.
          </div>
        </div>
      </footer>

      {/* Walkthrough Guide Modal */}
      <WalkthroughGuide open={showWalkthrough} onOpenChange={setShowWalkthrough} />

      {/* Coupon Dialog */}
      <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-teal-500" />
              Have a Coupon Code?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-gray-600">
              Enter your coupon code below for an additional discount, or continue without one.
            </p>
            
            <div className="flex gap-2">
              <Input
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter coupon code"
                className="font-mono"
                onKeyDown={(e) => e.key === 'Enter' && validateCoupon()}
              />
              <Button 
                variant="outline" 
                onClick={validateCoupon}
                disabled={couponValidating || !couponCode.trim()}
              >
                {couponValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
              </Button>
            </div>

            {appliedCoupon && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 font-medium">
                  <CheckCircle className="h-4 w-4" />
                  Coupon Applied: {appliedCoupon.discount_percentage}% OFF
                </div>
                {appliedCoupon.description && (
                  <p className="text-sm text-green-600 mt-1">{appliedCoupon.description}</p>
                )}
              </div>
            )}

            {earlyBird.is_active && !discountLink && (
              <div className={`p-3 rounded-lg border ${
                earlyBird.banner_theme === 'orange' ? 'bg-orange-50 border-orange-200' :
                earlyBird.banner_theme === 'red' ? 'bg-red-50 border-red-200' :
                earlyBird.banner_theme === 'green' ? 'bg-green-50 border-green-200' :
                earlyBird.banner_theme === 'purple' ? 'bg-purple-50 border-purple-200' :
                earlyBird.banner_theme === 'blue' ? 'bg-blue-50 border-blue-200' :
                earlyBird.banner_theme === 'dark' ? 'bg-gray-100 border-gray-300' :
                earlyBird.banner_theme === 'pink' ? 'bg-pink-50 border-pink-200' :
                earlyBird.banner_theme === 'gold' ? 'bg-yellow-50 border-yellow-200' :
                'bg-orange-50 border-orange-200'
              }`}>
                <p className={`text-sm ${
                  earlyBird.banner_theme === 'orange' ? 'text-orange-700' :
                  earlyBird.banner_theme === 'red' ? 'text-red-700' :
                  earlyBird.banner_theme === 'green' ? 'text-green-700' :
                  earlyBird.banner_theme === 'purple' ? 'text-purple-700' :
                  earlyBird.banner_theme === 'blue' ? 'text-blue-700' :
                  earlyBird.banner_theme === 'dark' ? 'text-gray-700' :
                  earlyBird.banner_theme === 'pink' ? 'text-pink-700' :
                  earlyBird.banner_theme === 'gold' ? 'text-yellow-700' :
                  'text-orange-700'
                }`}>
                  <strong>Note:</strong> {earlyBird.banner_title || 'Promotional'} discount ({earlyBird.discount_percentage}% OFF) is already applied. 
                  Coupon discounts are applied on top of the promotional price.
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowCouponDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                onClick={proceedToCheckout}
              >
                {appliedCoupon ? "Continue with Coupon" : "Continue without Coupon"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Trade Notify Me Modal */}
      <Dialog open={showTradeNotifyModal} onOpenChange={(open) => {
        setShowTradeNotifyModal(open);
        if (!open) {
          setTradeNotifySuccess(false);
          setTradeNotifyEmail('');
          setTradeNotifyName('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-500" />
              Get Notified - {selectedTradeForNotify?.name}
            </DialogTitle>
          </DialogHeader>
          
          {tradeNotifySuccess ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">You're on the list!</h3>
              <p className="text-gray-600">
                We'll email you when {selectedTradeForNotify?.name} is available.
              </p>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  {selectedTradeForNotify?.name} is coming soon. Be the first to know when it launches!
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email address *
                </label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={tradeNotifyEmail}
                  onChange={(e) => setTradeNotifyEmail(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name (optional)
                </label>
                <Input
                  type="text"
                  placeholder="Your name"
                  value={tradeNotifyName}
                  onChange={(e) => setTradeNotifyName(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <Button 
                onClick={handleTradeNotifySubmit}
                disabled={tradeNotifySubmitting || !tradeNotifyEmail}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                {tradeNotifySubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Registering...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Notify Me
                  </>
                )}
              </Button>
              
              <p className="text-xs text-gray-500 text-center">
                We'll only email you about {selectedTradeForNotify?.name} updates. No spam.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Paid card capture modal — fires for logged-in users upgrading
          from the landing page pricing section. Replaces the legacy
          Payment-Link redirect which silently failed to create subs. */}
      {selectedTier && (
        <PaidCardCapture
          open={showPaidCardModal}
          onOpenChange={setShowPaidCardModal}
          tier={selectedTier}
          authToken={localStorage.getItem("token")}
          tierPrice={({ solo: 9.99, pro: 29.99, crew: 74.99 })[selectedTier]}
          couponCode={appliedCoupon?.code}
          couponDiscountPct={appliedCoupon?.discount_percentage}
          onSuccess={handlePaidSubscribed}
        />
      )}
    </div>
  );
};

export default LandingPage;
